import { Fragment, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext, Controller } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Typography } from '@components/Typography';
import { Select } from '@components/Select';
import { useTranslation } from '@hooks';
import { ROLE, ACCESS_LEVEL } from '@constants/user';

const Wrapper = styled('div')({
    display: 'flex',
    gap: 24,
    marginTop: -8,
    marginBottom: 8,
    '@media (max-width: 768px)': {
        marginTop: -16,
    },
});

const StyledRadioGroup = styled(RadioGroup)({
    flexDirection: 'row',
    alignItems: 'center',
    '& .MuiFormControlLabel-root': {
        marginRight: 0,
        marginLeft: '-3px !important',
    },
});

export const LOCATION_TYPE = {
    LOCATION: '0',
    BRAND: '1',
};

/**
 *
 * @param {Array} brandsOptions
 * @param {Array} locationsOptions
 * @param {Object} data - entity from edit modals
 * @param {boolean} disabled
 * @param {boolean} isView - allow low access level users see locations nad brand assets
 * @param {Function} updatedLocationsCb - for setting the depending on location values (for brands - selected main location id)
 */
const ComboLocationSelect = ({
    brandsOptions,
    locationsOptions,
    data,
    disabled,
    isView,
    updatedLocationCb = () => {},
    updatedLocationTypeCb = () => {},
}) => {
    const user = useSelector(state => state.auth.user);
    const { control, watch, setValue } = useFormContext();
    const brands = useSelector(state => state.brands.data);
    const { t } = useTranslation();

    const locationType = watch('locationType');
    const brandAccess = watch('brandAccess');
    const valuesAlreadySetToForm = watch('valuesAlreadySetToForm');

    useEffect(() => {
        const hasRestrictedAccess =
            (user?.role === ROLE.USER || user?.role === ROLE.ADMIN) && user?.accessLevel === ACCESS_LEVEL.LOCATION;
        setValue('brandAccess', !hasRestrictedAccess);
        if (hasRestrictedAccess && !isView) {
            setValue('locationType', LOCATION_TYPE.LOCATION, { shouldValidate: true });
        }

        if (data && !valuesAlreadySetToForm) {
            setValue('brandId', data?.brandId || '', { shouldValidate: true });
            setValue('locationId', data?.locationId || '', { shouldValidate: true });
            setValue('locationType', data?.locationId ? LOCATION_TYPE.LOCATION : LOCATION_TYPE.BRAND, {
                shouldValidate: true,
            });
            setValue('valuesAlreadySetToForm', true);
        }
    }, [setValue, user, data]);

    return brandAccess || isView ? (
        <div className="combo-location-select">
            <Controller
                control={control}
                name="locationType"
                render={() => (
                    <Wrapper className="location-type">
                        {[
                            { id: LOCATION_TYPE.BRAND, name: t('common.brand') },
                            { id: LOCATION_TYPE.LOCATION, name: t('common.location') },
                        ].map(type => (
                            <Fragment key={type.id}>
                                <StyledRadioGroup
                                    value={locationType}
                                    onChange={e => {
                                        setValue('locationType', e.target.value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                        updatedLocationTypeCb();
                                    }}
                                >
                                    <FormControlLabel
                                        value={type.id}
                                        control={
                                            <Radio
                                                sx={{
                                                    color: 'rgba(0, 0, 0, 0.6)',
                                                    padding: '6px',
                                                    '&.Mui-checked': { color: '#1F4C5C' },
                                                }}
                                                disabled={disabled}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" sx={{ marginLeft: '10px' }}>
                                                {type.name}
                                            </Typography>
                                        }
                                        disabled={disabled}
                                    ></FormControlLabel>
                                </StyledRadioGroup>
                            </Fragment>
                        ))}
                    </Wrapper>
                )}
            />
            {locationType === LOCATION_TYPE.BRAND && (
                <Controller
                    control={control}
                    name="brandId"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            onChange={({ target: { value } }) => {
                                setValue('brandId', value, { shouldValidate: true, shouldDirty: true });
                                setValue('locationId', '', { shouldDirty: true, shouldValidate: true });
                                const mainLocationId = brands
                                    .find(b => b.id === value)
                                    ?.locations.find(l => l.isMainLocation)?.id;
                                updatedLocationCb(mainLocationId);
                            }}
                            ref={ref}
                            label={t('common.brand')}
                            fullWidth
                            required
                            options={brandsOptions}
                            disabled={disabled}
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
            )}
            {locationType === LOCATION_TYPE.LOCATION && (
                <Controller
                    control={control}
                    name="locationId"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            ref={ref}
                            onChange={({ target: { value } }) => {
                                setValue('locationId', value, { shouldValidate: true, shouldDirty: true });
                                setValue('brandId', '', { shouldDirty: true, shouldValidate: true });
                                updatedLocationCb(value);
                            }}
                            options={locationsOptions}
                            required
                            label={t('common.location')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            groupBased
                            disabled={disabled}
                        />
                    )}
                />
            )}
        </div>
    ) : (
        <div className="combo-location-select">
            <Controller
                control={control}
                name="locationId"
                render={({ field, fieldState: { error }, ref }) => (
                    <Select
                        {...field}
                        ref={ref}
                        onChange={({ target: { value } }) => {
                            setValue('locationId', value, { shouldValidate: true, shouldDirty: true });
                            setValue('brandId', '', { shouldDirty: true, shouldValidate: true });
                            updatedLocationCb(value);
                        }}
                        options={locationsOptions}
                        required
                        label={t('common.location')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        groupBased
                        disabled={disabled}
                    />
                )}
            />
        </div>
    );
};

export default ComboLocationSelect;
